import React, { useState, useEffect, useRef } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, Brain, Wand2, Bot, ArrowRight, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import gsap from 'gsap';
import FeatureTourModal from '../components/FeatureTourModal';

interface LoginProps {
  onSwitchToRegister: () => void;
}

const FEATURES = [
  {
    icon: Brain,
    label: 'Deep Analysis',
    title: 'Deep Analysis',
    subtitle: 'See What Others Miss',
    description: 'Get detailed reports in seconds, not hours. 10+ AI models working together to catch every detail humans overlook.',
  },
  {
    icon: Bot,
    label: 'AI Agents',
    title: 'AI Agents',
    subtitle: 'Automate Everything',
    description: 'Build workflows once, run them forever. Your AI team processes thousands of images while you focus on decisions.',
  },
  {
    icon: Wand2,
    label: 'Precision Tools',
    title: 'Precision Tools',
    subtitle: 'Create & Edit Faster',
    description: 'Generate, enhance, and transform images in clicks. Professional results without the learning curve.',
  },
];

const DEFAULT_CONTENT = {
  title: 'Pioneer in Professional',
  subtitle: 'Image Analysis',
  description: 'Cut analysis time. One platform, multiple AI models, instant insights. Join the teams already saving hours every week.',
};

export default function Login({ onSwitchToRegister }: LoginProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState<number | null>(null);
  const [showTour, setShowTour] = useState(false);

  // Refs for GSAP
  const containerRef = useRef<HTMLDivElement>(null);
  const videoCardRef = useRef<HTMLDivElement>(null);
  const brandRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const loginCardRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  // Get current content based on hover state
  const currentContent = activeFeature !== null ? FEATURES[activeFeature] : DEFAULT_CONTENT;

  // Animate content change
  const handleFeatureHover = (index: number | null) => {
    // Animate out
    gsap.to([titleRef.current, descriptionRef.current], {
      opacity: 0,
      y: -10,
      duration: 0.15,
      ease: 'power2.in',
      onComplete: () => {
        setActiveFeature(index);
        // Animate in
        gsap.fromTo(
          [titleRef.current, descriptionRef.current],
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out', stagger: 0.05 }
        );
      },
    });
  };

  // Entrance animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set([brandRef.current, videoCardRef.current, contentRef.current, loginCardRef.current], {
        opacity: 0,
        y: 30,
      });
      gsap.set(featuresRef.current?.children || [], { opacity: 0, y: 20 });

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.to(brandRef.current, { opacity: 1, y: 0, duration: 0.8 }, 0.2)
        .to(videoCardRef.current, { opacity: 1, y: 0, duration: 1 }, 0.4)
        .to(contentRef.current, { opacity: 1, y: 0, duration: 0.8 }, 0.6)
        .to(loginCardRef.current, { opacity: 1, y: 0, duration: 0.9 }, 0.5)
        .to(featuresRef.current?.children || [], {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
        }, 0.9);

    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-8"
    >
      <div className="w-full max-w-6xl">
        {/* Main Card Container */}
        <div className="bg-white border-4 border-brutal-black shadow-[8px_8px_0px_0px_#000] overflow-hidden">
          <div className="flex flex-col lg:flex-row">

            {/* Left Side - Hero Content */}
            <div className="lg:w-1/2 p-6 sm:p-8 lg:p-10 flex flex-col">

              {/* Brand Header */}
              <div ref={brandRef} className="mb-8">
                <div className="flex items-center gap-3 mb-1">
                  {/* Minimal Logo */}
                  <div className="w-10 h-10 bg-brutal-black flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                      <path d="M6 4h12v3H9v4h7v3H9v6H6V4z" fill="#FFE500" />
                    </svg>
                  </div>
                  <span className="text-2xl font-black uppercase tracking-tight text-brutal-black">
                    FLUX <span className="text-brutal-black/40">Studio</span>
                  </span>
                </div>
              </div>

              {/* Video Card */}
              <div
                ref={videoCardRef}
                className="relative flex-1 min-h-[280px] sm:min-h-[320px] lg:min-h-[360px] mb-6 border-3 border-brutal-black overflow-hidden group"
                style={{ boxShadow: '6px 6px 0px 0px #000' }}
              >
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                >
                  <source src="/bgvideo.mp4" type="video/mp4" />
                </video>

                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brutal-black/60 via-transparent to-transparent" />

                {/* Video caption */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <p className="text-white/90 text-xs sm:text-sm font-medium uppercase tracking-wider">
                    Excellence is not optional
                  </p>
                </div>

                {/* Corner accent */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-brutal-yellow border-2 border-brutal-black rotate-12 opacity-90" />
              </div>

              {/* Description - Dynamic content */}
              <div ref={contentRef} className="mb-6 min-h-[120px]">
                <h2 ref={titleRef} className="text-xl sm:text-2xl font-black uppercase text-brutal-black leading-tight mb-3">
                  {currentContent.title}
                  <span className="block text-brutal-black/50">{currentContent.subtitle}</span>
                </h2>
                <p ref={descriptionRef} className="text-sm sm:text-base text-brutal-black/60 font-medium leading-relaxed">
                  {currentContent.description}
                </p>
              </div>

              {/* Features */}
              <div ref={featuresRef} className="flex flex-wrap gap-2 mb-6">
                {FEATURES.map((feature, i) => (
                  <div
                    key={i}
                    onMouseEnter={() => handleFeatureHover(i)}
                    onMouseLeave={() => handleFeatureHover(null)}
                    className={`flex items-center gap-2 px-3 py-2 border-2 transition-all duration-200 cursor-pointer
                      ${activeFeature === i
                        ? 'bg-brutal-yellow border-brutal-black'
                        : 'bg-brutal-black/5 border-brutal-black/10 hover:border-brutal-black/30 hover:bg-brutal-yellow/20'
                      }`}
                  >
                    <feature.icon className={`h-4 w-4 ${activeFeature === i ? 'text-brutal-black' : 'text-brutal-black/70'}`} />
                    <span className={`text-xs font-bold uppercase ${activeFeature === i ? 'text-brutal-black' : 'text-brutal-black/70'}`}>
                      {feature.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Secondary CTA */}
              <button
                className="flex items-center gap-2 text-brutal-black/60 hover:text-brutal-black transition-colors group"
                onClick={() => setShowTour(true)}
              >
                <div className="w-8 h-8 rounded-full border-2 border-brutal-black/30 group-hover:border-brutal-black group-hover:bg-brutal-yellow/20 flex items-center justify-center transition-all">
                  <Play className="h-3 w-3 ml-0.5" />
                </div>
                <span className="text-sm font-medium">See how it works</span>
              </button>
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px bg-brutal-black/10" />
            <div className="lg:hidden h-px bg-brutal-black/10 mx-6" />

            {/* Right Side - Login Form */}
            <div className="lg:w-1/2 p-6 sm:p-8 lg:p-10 bg-slate-50/50 flex items-center justify-center">
              <div ref={loginCardRef} className="w-full max-w-sm">

                {/* Login Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-black uppercase mb-1">Welcome</h3>
                  <p className="text-brutal-black/50 text-sm font-medium">Sign in to continue</p>
                </div>

                {/* Error */}
                {error && (
                  <div
                    className="mb-5 p-3 bg-brutal-red/10 border-2 border-brutal-red/30 text-brutal-red font-bold text-sm"
                  >
                    {error}
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase mb-2 text-brutal-black/70">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brutal-black/30 pointer-events-none" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="     your@email.com"
                        required
                        className="w-full pl-12 pr-4 py-3 border-2 border-brutal-black/20 bg-white font-medium text-sm focus:outline-none focus:border-brutal-black/50 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-xs font-bold uppercase mb-2 text-brutal-black/70">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brutal-black/30 pointer-events-none" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="     ••••••••"
                        required
                        className="w-full pl-12 pr-12 py-3 border-2 border-brutal-black/20 bg-white font-medium text-sm focus:outline-none focus:border-brutal-black/50 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-brutal-black/30 hover:text-brutal-black/60 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 mt-2 bg-brutal-black text-white border-2 border-brutal-black font-bold uppercase text-sm cursor-pointer transition-all duration-200 hover:bg-brutal-yellow hover:text-brutal-black active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </button>
                </form>

                {/* Register link */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-brutal-black/50">
                    New here?{' '}
                    <button
                      onClick={onSwitchToRegister}
                      className="font-bold text-brutal-black hover:text-brutal-magenta transition-colors"
                    >
                      Create account
                    </button>
                  </p>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-brutal-black/10 text-center">
                  <a
                    href="https://www.instagram.com/charlyx.mp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brutal-black/30 font-medium uppercase tracking-wider hover:text-brutal-magenta transition-colors"
                  >
                    Crafted by Charly
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Tour Modal */}
      {showTour && <FeatureTourModal onClose={() => setShowTour(false)} />}
    </div>
  );
}
